import type { SettingsBadgeKey } from '../../lib/settingsNav';

/**
 * The three Settings surfaces that show a "needs attention" badge —
 * the global Sidebar entry, the Settings home cards, and the in-area rail
 * (AdminLayout) — render the SAME two badges (tickets with no board column,
 * tickets to push) in two shapes. This component owns their meaning so it lives
 * in ONE place: the colour, the count→text wording, and the actionable tooltip.
 * Counts come from {@link useSettingsBadges}.
 *
 * The `unmapped` key is a legacy name kept for the API route
 * (`/statuses/unmapped-count`) and the nav item's `badge` field. It counts
 * `JiraTicket.statusId IS NULL` — tickets sitting in NO board column, usually
 * created in Avium via "Add data" without picking a status, or imported with a
 * blank status. It does NOT mean "a status we failed to map": Status Model 1.0
 * (AVM-159) retired status→status routing, so every source status now passes
 * through 1:1 into its own same-named column and can never be "unmapped". The
 * copy below says what the count actually is; the fix is placing those tickets
 * on the board (Settings → Work model → Status Mapping → Unmapped Tickets).
 */
export type SettingsBadgeVariant =
  | 'compact' // rail + sidebar: tiny solid glyph / count pill
  | 'label';  // home cards: soft descriptive pill ("3 unmapped")

interface BadgeMeta {
  /** Solid treatment for the `compact` variant. */
  solid: string;
  /** Soft treatment for the `label` variant. */
  soft: string;
  /** Compact form: a fixed "!" dot vs. a count pill. */
  compactShape: 'dot' | 'count';
  /** Descriptive text for the `label` variant. */
  label: (n: number) => string;
  /** Actionable tooltip — shared by every surface so the copy can't drift. */
  title: (n: number) => string;
}

const META: Record<SettingsBadgeKey, BadgeMeta> = {
  unmapped: {
    solid: 'bg-amber-400 text-amber-900',
    soft: 'bg-amber-100 text-amber-800',
    compactShape: 'dot',
    label: n => `${n} without a status`,
    title: n =>
      `${n} ticket${n !== 1 ? 's are' : ' is'} not in any board column yet — ` +
      `open Settings → Work model → Status Mapping to place ${n !== 1 ? 'them' : 'it'}`,
  },
  jiraModified: {
    solid: 'bg-orange-500 text-white',
    soft: 'bg-orange-100 text-orange-800',
    compactShape: 'count',
    label: n => `${n} to push`,
    title: n =>
      `${n} ticket${n !== 1 ? 's' : ''} edited in Avium and not yet pushed — ` +
      `open Settings → Data Sources → Push to Jira`,
  },
};

export default function SettingsBadge({
  badgeKey,
  count,
  variant = 'compact',
  className = '',
}: {
  badgeKey: SettingsBadgeKey;
  count: number;
  variant?: SettingsBadgeVariant;
  /** Extra classes for positioning (e.g. `ml-auto` in the rail). */
  className?: string;
}) {
  if (count <= 0) return null;
  const meta = META[badgeKey];
  const title = meta.title(count);

  if (variant === 'label') {
    return (
      <span
        className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none ${meta.soft} ${className}`}
        title={title}
      >
        {meta.label(count)}
      </span>
    );
  }

  if (meta.compactShape === 'dot') {
    return (
      <span
        className={`text-[10px] font-black rounded-full w-4 h-4 flex items-center justify-center shrink-0 ${meta.solid} ${className}`}
        title={title}
      >
        !
      </span>
    );
  }

  return (
    <span
      className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none shrink-0 ${meta.solid} ${className}`}
      title={title}
    >
      {count}
    </span>
  );
}
